import { Box, IconButton, ListItem, Tooltip, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { ColumnKey, COLUMN_LABELS } from './columnDefs';
import { useColumnOrderRow } from './useColumnOrderRow';

type ColumnOrderRowProps = {
  columnKey: ColumnKey;
  index: number;
  total: number;
  disabled: boolean;
  edge: Edge | null | undefined;
  onEdgeChange: (columnKey: ColumnKey, edge: Edge | null) => void;
  onMove: (index: number, delta: number) => void;
};

export function ColumnOrderRow({
  columnKey,
  index,
  total,
  disabled,
  edge,
  onEdgeChange,
  onMove,
}: ColumnOrderRowProps) {
  const ref = useColumnOrderRow(columnKey, disabled, onEdgeChange);
  const label = COLUMN_LABELS[columnKey];

  return (
    <ListItem
      ref={ref}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 12,
        bgcolor: 'background.paper',
        ...(edge === 'top' && {
          borderTop: '2px solid',
          borderTopColor: 'primary.main',
        }),
        ...(edge === 'bottom' && {
          borderBottom: '2px solid',
          borderBottomColor: 'primary.main',
        }),
      }}
    >
      <DragIndicatorIcon
        sx={{ mr: 1, color: 'text.disabled' }}
        aria-hidden="true"
      />
      <Typography sx={{ flexGrow: 1 }}>{label}</Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title={`Move ${label} up`}>
          <span>
            <IconButton
              size="small"
              aria-label={`Move ${label} up`}
              disabled={disabled || index === 0}
              onClick={() => onMove(index, -1)}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={`Move ${label} down`}>
          <span>
            <IconButton
              size="small"
              aria-label={`Move ${label} down`}
              disabled={disabled || index === total - 1}
              onClick={() => onMove(index, 1)}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </ListItem>
  );
}
